// src/pages/finance-portal/stripe-treasury/StripeTreasury.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Alert, CircularProgress, Skeleton, Tooltip, Tabs, Tab,
    Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val, currency = 'usd') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format((val || 0) / 100);
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(typeof dateStr === 'number' ? dateStr * 1000 : dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const TX_STATUS_COLORS = {
    posted: { bg: '#dcfce7', color: '#166534' },
    open: { bg: '#dbeafe', color: '#1e40af' },
    void: { bg: '#fecaca', color: '#991b1b' },
};

export default function StripeTreasury() {
    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [outboundPayments, setOutboundPayments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [txLoading, setTxLoading] = useState(false);
    const [obLoading, setObLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [tabValue, setTabValue] = useState(0);

    // Fund dialog
    const [fundOpen, setFundOpen] = useState(false);
    const [fundForm, setFundForm] = useState({ amount: '', description: '' });
    const [funding, setFunding] = useState(false);

    const fetchAccounts = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/stripe/treasury/financial-accounts');
            if (res.data.success) {
                const accts = res.data.data || [];
                setAccounts(accts);
                if (accts.length > 0 && !selectedAccount) {
                    setSelectedAccount(accts[0]);
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load financial accounts');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchTransactions = useCallback(async () => {
        if (!selectedAccount) return;
        setTxLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/stripe/treasury/transactions', {
                params: { financial_account_id: selectedAccount.id }
            });
            if (res.data.success) {
                setTransactions(res.data.data || []);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load transactions');
        } finally {
            setTxLoading(false);
        }
    }, [selectedAccount]);

    const fetchOutboundPayments = useCallback(async () => {
        if (!selectedAccount) return;
        setObLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/stripe/treasury/outbound-payments', {
                params: { financial_account_id: selectedAccount.id }
            });
            if (res.data.success) {
                setOutboundPayments(res.data.data || []);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load outbound payments');
        } finally {
            setObLoading(false);
        }
    }, [selectedAccount]);

    useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
    useEffect(() => {
        if (selectedAccount) {
            fetchTransactions();
            fetchOutboundPayments();
        }
    }, [selectedAccount, fetchTransactions, fetchOutboundPayments]);

    const handleFund = async () => {
        if (!selectedAccount) return;
        setFunding(true);
        try {
            await axiosInstance.post('/api/finance/stripe/treasury/inbound-transfers', {
                financial_account_id: selectedAccount.id,
                amount: Math.round(parseFloat(fundForm.amount) * 100),
                currency: 'usd',
                description: fundForm.description || undefined,
            });
            setSuccess('Inbound transfer initiated');
            setFundOpen(false);
            setFundForm({ amount: '', description: '' });
            fetchAccounts();
            fetchTransactions();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to initiate transfer');
        } finally {
            setFunding(false);
        }
    };

    const getBalanceCards = () => {
        if (!selectedAccount?.balance) return [];
        const b = selectedAccount.balance;
        return [
            { label: 'Cash', amount: b.cash?.usd || 0, color: '#22c55e' },
            { label: 'Inbound Pending', amount: b.inbound_pending?.usd || 0, color: '#3b82f6' },
            { label: 'Outbound Pending', amount: b.outbound_pending?.usd || 0, color: '#f59e0b' },
        ];
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <AccountBalanceIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Stripe Treasury</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Financial accounts, transfers & payments</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => { fetchAccounts(); if (selectedAccount) { fetchTransactions(); fetchOutboundPayments(); } }}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, color: '#fff', boxShadow: 'none' }}
                        >
                            Refresh
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFundOpen(true)} disabled={!selectedAccount}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, color: '#fff', boxShadow: 'none' }}
                        >
                            Fund Account
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Account Selector */}
                {accounts.length > 1 && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        {accounts.map(acct => (
                            <Chip key={acct.id} label={acct.id}
                                onClick={() => setSelectedAccount(acct)}
                                sx={{
                                    fontFamily, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                                    bgcolor: selectedAccount?.id === acct.id ? '#dcfce7' : '#f1f5f9',
                                    color: selectedAccount?.id === acct.id ? '#166534' : '#64748b',
                                    border: selectedAccount?.id === acct.id ? '1px solid #22c55e' : '1px solid #e2e8f0',
                                }}
                            />
                        ))}
                    </Box>
                )}

                {/* Balance Cards */}
                {isLoading ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
                        {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={80} sx={{ borderRadius: '14px' }} />)}
                    </Box>
                ) : selectedAccount && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
                        {getBalanceCards().map(card => (
                            <Paper key={card.label} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{card.label}</Typography>
                                <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 800, color: card.color, mt: 0.5 }}>
                                    {formatCurrency(card.amount)}
                                </Typography>
                            </Paper>
                        ))}
                    </Box>
                )}

                {/* Tabs: Transactions | Outbound Payments */}
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2,
                    '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.85rem', minHeight: 40 },
                    '& .Mui-selected': { color: `${ACCENT} !important` },
                    '& .MuiTabs-indicator': { bgcolor: ACCENT },
                }}>
                    <Tab label="Transactions" />
                    <Tab label="Outbound Payments" />
                </Tabs>

                {/* Transactions Tab */}
                {tabValue === 0 && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {['Transaction ID', 'Amount', 'Type', 'Status', 'Created'].map(h => (
                                            <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {txLoading ? (
                                        [1, 2, 3].map(i => <TableRow key={i}><TableCell colSpan={5}><Skeleton height={36} /></TableCell></TableRow>)
                                    ) : transactions.length === 0 ? (
                                        <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No transactions found</Typography>
                                        </TableCell></TableRow>
                                    ) : transactions.map(tx => {
                                        const statusStyle = TX_STATUS_COLORS[tx.status] || { bg: '#f1f5f9', color: '#64748b' };
                                        return (
                                            <TableRow key={tx.id} hover>
                                                <TableCell sx={{ fontFamily, fontSize: '0.8rem', fontFamily: 'monospace' }}>{tx.id}</TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: (tx.amount || 0) >= 0 ? '#166534' : '#991b1b' }}>
                                                    {formatCurrency(tx.amount, tx.currency || 'usd')}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', textTransform: 'capitalize' }}>
                                                    {tx.flow_type?.replace(/_/g, ' ') || '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={tx.status} size="small" sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22, bgcolor: statusStyle.bg, color: statusStyle.color }} />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{formatDate(tx.created)}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                )}

                {/* Outbound Payments Tab */}
                {tabValue === 1 && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {['Payment ID', 'Amount', 'Description', 'Status', 'Created'].map(h => (
                                            <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {obLoading ? (
                                        [1, 2, 3].map(i => <TableRow key={i}><TableCell colSpan={5}><Skeleton height={36} /></TableCell></TableRow>)
                                    ) : outboundPayments.length === 0 ? (
                                        <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No outbound payments found</Typography>
                                        </TableCell></TableRow>
                                    ) : outboundPayments.map(op => (
                                        <TableRow key={op.id} hover>
                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem', fontFamily: 'monospace' }}>{op.id}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>
                                                {formatCurrency(op.amount, op.currency || 'usd')}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{op.description || '-'}</TableCell>
                                            <TableCell>
                                                <Chip label={op.status} size="small" sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22, bgcolor: '#e0e7ff', color: '#3730a3' }} />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{formatDate(op.created)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                )}
            </Box>

            {/* Fund Account Dialog */}
            <Dialog open={fundOpen} onClose={() => setFundOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>Fund Account</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                    <TextField label="Amount" value={fundForm.amount}
                        onChange={e => setFundForm(prev => ({ ...prev, amount: e.target.value }))}
                        fullWidth size="small" type="number"
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            sx: { fontFamily, borderRadius: '10px' },
                            inputProps: { min: 1, step: '0.01' },
                        }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                    <TextField label="Description (optional)" value={fundForm.description}
                        onChange={e => setFundForm(prev => ({ ...prev, description: e.target.value }))}
                        fullWidth size="small"
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setFundOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleFund}
                        disabled={funding || !fundForm.amount || parseFloat(fundForm.amount) < 1}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {funding ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Fund'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
